import Box from '@mui/material/Box';
import Chip, { type ChipProps } from '@mui/material/Chip';
import { deepOrange } from '@mui/material/colors';
import { ResizableInput } from 'components/input/ResizableInput';
import { Suspense, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ContentType } from 'utils/getContentType';
import type { KeyboardEvent } from 'react';
import DOMPurify from 'dompurify';
import { removeSpecialCharacters } from 'components/library/save-asset-dialog/removeSpecialCharacters';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId, selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import useUpdateDocumentProperties from 'components/editor/hooks/useUpdateDocumentProperties';
import i18next from 'i18next';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { contentQueries } from 'services/content/contentQueries';
import { useDispatch, useSelector } from 'react-redux';
import { selectVariableByName, upsertVariableValue } from '../grid/reduxStore/allVariableSlice';
import { VariableName } from 'components/editor/SidePanel/Variables/interfaces';
import { VariableDomain } from 'services/entities/VariablesEntity';
import type { RootState } from '../grid/reduxStore/Store';
import { editorMenuTitleTestIds } from './EditorMenuTitle.test-ids';
import './styles.less';

const getChipPropsByType = (): Record<ContentType, ChipProps> => ({
  [ContentType.DOCUMENT]: {
    label: String(i18next.t('editor.toolbar.chipLabel.document')),
    sx: {
      display: 'none',
    },
  },
  [ContentType.TEMPLATE]: {
    label: String(i18next.t('editor.toolbar.chipLabel.template')),
    sx: {
      color: 'secondary.contrastText',
      backgroundColor: 'secondary.main',
    },
  },
  [ContentType.SECTION]: {
    label: String(i18next.t('editor.toolbar.chipLabel.section')),
    sx: {
      color: deepOrange[600],
      backgroundColor: deepOrange[50],
    },
  },
});

const getDefaultTitleByType = (): Record<ContentType, string> => ({
  [ContentType.DOCUMENT]: i18next.t('editor.default_document_title'),
  [ContentType.TEMPLATE]: i18next.t('editor.editor_menu.default_template_title'),
  [ContentType.SECTION]: i18next.t('editor.default_content_section_title'),
});

const blurOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') {
    event.currentTarget.blur();
  }
};

export const sanitizeTitle = (value: string) => removeSpecialCharacters(DOMPurify.sanitize(value)).substring(0, 99);

function EditorMenuTitleLoader() {
  const contentId = useAppSelector(selectContentId);
  const contentType = useAppSelector(selectContentType);
  const dispatch = useDispatch();
  const { data } = useSuspenseQuery(contentQueries.contentMetadataById(contentType, contentId));
  const reduxName = useSelector((state: RootState) => selectVariableByName(state, VariableName.ProposalName)?.value);
  const { mutate } = useUpdateDocumentProperties();
  const queryClient = useQueryClient();

  const defaultTitle = getDefaultTitleByType()[contentType];

  const fetchedName = data?.name;
  useEffect(() => {
    if (fetchedName !== undefined && fetchedName !== reduxName) {
      dispatch(
        upsertVariableValue({
          shape: { type: 'string' },
          variableName: VariableName.ProposalName,
          newValue: fetchedName,
          parent: VariableDomain.DOCUMENT,
        })
      );
    }
  }, [dispatch, fetchedName, reduxName]);

  const resolvedTitle = String(reduxName ?? fetchedName ?? '');
  const [liveTitle, setLiveTitle] = useState(resolvedTitle);
  const [syncedTitle, setSyncedTitle] = useState(resolvedTitle);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const tabTitle = resolvedTitle || defaultTitle;

  if (!hasUnsavedEdits && resolvedTitle !== syncedTitle) {
    setSyncedTitle(resolvedTitle);
    setLiveTitle(resolvedTitle);
  }

  function onBlur() {
    if (!hasUnsavedEdits) {
      if (!liveTitle.trim() && resolvedTitle) {
        setLiveTitle(resolvedTitle);
      }
      return;
    }

    const value = sanitizeTitle(liveTitle.trim() || defaultTitle);
    setHasUnsavedEdits(false);
    setLiveTitle(value);
    mutate({ contentId, title: value });
    dispatch(
      upsertVariableValue({
        shape: { type: 'string' },
        variableName: VariableName.ProposalName,
        newValue: value,
        parent: VariableDomain.DOCUMENT,
      })
    );
    queryClient.setQueryData(contentQueries.contentMetadataById(contentType, contentId).queryKey, (old) =>
      old ? { ...old, name: value } : old
    );
  }

  return (
    <>
      <Helmet>
        <title>{tabTitle} | Editor</title>
      </Helmet>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          '& input.document-title': {
            border: 'none',
            outline: '1px solid transparent',
            '&:hover': {
              outline: '1px solid #00b8d1',
            },
          },
        }}
      >
        <ResizableInput
          data-testid={editorMenuTitleTestIds.documentTitle}
          className="document-title"
          style={{ margin: 0, paddingLeft: 0 }}
          onKeyDown={blurOnEnter}
          maxLength={100}
          placeholder={defaultTitle}
          value={liveTitle}
          onChange={(e) => {
            setHasUnsavedEdits(true);
            setLiveTitle(e.target.value);
          }}
          onBlur={onBlur}
        />
        <Chip {...getChipPropsByType()[contentType]} size="small" />
      </Box>
    </>
  );
}

export function EditorMenuTitle() {
  return (
    <Suspense>
      <EditorMenuTitleLoader />
    </Suspense>
  );
}
